import { toPng } from 'html-to-image'

// Default placeholder SVG for broken/failed images (gray user avatar)
const DEFAULT_IMAGE_PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2394a3b8"%3E%3Cpath d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/%3E%3C/svg%3E'

/**
 * Check if an image element is broken (failed to load or has invalid data)
 * @param {HTMLImageElement} img - The image element to check
 * @returns {boolean} - True if the image is broken
 */
function isImageBroken(img) {
    // Image hasn't loaded or has no dimensions
    if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
        return true
    }
    // Check if src contains HTML error page (e.g., 429 error from CDN)
    // This happens when CDNs return HTML error pages instead of images
    if (img.src && img.src.startsWith('data:text/html')) {
        return true
    }
    return false
}

/**
 * Composable for exporting DOM elements as images
 */
export function useImageExport() {
    /**
     * Downloads a DOM element as a PNG image
     * @param {HTMLElement} element - The DOM element to export
     * @param {string} filename - The filename for the downloaded image (without extension)
     * @returns {Promise<boolean>} - True if export succeeded, false otherwise
     */
    const downloadAsImage = async (element, filename = 'export') => {
        if (!element) {
            console.error('No element provided for image export')
            return false
        }

        try {
            // Pre-process: Fix broken images in the original element before cloning
            // This handles images that already failed to load (e.g., 429 errors)
            const images = element.querySelectorAll('img')
            const originalSrcs = new Map()

            images.forEach((img) => {
                if (isImageBroken(img)) {
                    // Store original src so we can restore it after export
                    originalSrcs.set(img, img.src)
                    // Replace with placeholder
                    img.src = DEFAULT_IMAGE_PLACEHOLDER
                }
            })

            // Define padding value
            const padding = 24

            // Get element dimensions
            const rect = element.getBoundingClientRect()

            // Convert the element to a PNG data URL
            const dataUrl = await toPng(element, {
                cacheBust: true,
                pixelRatio: 2, // Higher quality export (2x resolution)
                backgroundColor: '#ffffff', // White background
                width: rect.width + (padding * 2), // Add padding to width
                height: rect.height + (padding * 2), // Add padding to height
                style: {
                    padding: `${padding}px`, // Add padding to the cloned element
                },
                // Placeholder for failed image fetches during export (CORS issues)
                imagePlaceholder: DEFAULT_IMAGE_PLACEHOLDER,
                // Skip external fonts that might fail to load
                skipFonts: true,
                filter: (node) => {
                    // Exclude elements with data-html2canvas-ignore attribute
                    if (node.dataset && node.dataset.html2canvasIgnore !== undefined) {
                        return false
                    }
                    // Exclude elements with the export-ignore class
                    if (node.classList && node.classList.contains('export-ignore')) {
                        return false
                    }
                    return true
                },
            })

            // Restore original src values
            originalSrcs.forEach((src, img) => {
                img.src = src
            })

            // Create a temporary link element and trigger download
            const link = document.createElement('a')
            link.download = `${filename}.png`
            link.href = dataUrl
            link.click()

            return true
        } catch (error) {
            console.error('Failed to export image:', error)
            // Don't rethrow - this prevents Vue's "Unhandled error during execution of native event handler"
            // The error is logged, and calling code can check the return value
            return false
        }
    }

    return {
        downloadAsImage,
    }
}

